import { z } from 'zod';
import { KeenOutcome } from './domain.js';

// Help train Jabberr (docs/research/2026-09-24-classifier-kit-plan.md): people label real keen decision
// moments, and the labels train and score every decider (the GLM prompt and threshold now, a small classifier next).

/** What Jabberr should have done right after the moment's last line. */
export const KeenLabel = z.enum(['speak', 'quiet', 'unsure']);
export type KeenLabel = z.infer<typeof KeenLabel>;

/** Optional why, feeding the evaluator's cost weights. */
export const KeenLabelReason = z.enum(['addressed', 'open_question', 'correction', 'between_people']);
export type KeenLabelReason = z.infer<typeof KeenLabelReason>;

export const LabelSource = z.enum(['meeting', 'text']);
export type LabelSource = z.infer<typeof LabelSource>;

export const LabelLine = z.object({ speaker: z.string(), text: z.string() });
export type LabelLine = z.infer<typeof LabelLine>;

/** One card: a decision moment, its last line being the one judged. No decider verdict: labels stay blind. */
export const LabelDeckItem = z.object({
  key: z.string().min(1),
  source: LabelSource,
  projectName: z.string(),
  /** The lines leading up to the moment, oldest first; the last is the judged line. */
  context: z.array(LabelLine).min(1),
  /** Seconds of silence before the judged line, when known (meetings). */
  gapBeforeS: z.number().nonnegative().nullable(),
});
export type LabelDeckItem = z.infer<typeof LabelDeckItem>;

export const LabelDeckFilter = z.enum(['all', 'short']);
export type LabelDeckFilter = z.infer<typeof LabelDeckFilter>;

export const LabelDeckResponse = z.object({
  items: z.array(LabelDeckItem),
  /** Moments this person has not labelled yet, including the ones returned. */
  remaining: z.number().int().nonnegative(),
  labelled: z.number().int().nonnegative(),
});
export type LabelDeckResponse = z.infer<typeof LabelDeckResponse>;

export const PutLabelRequest = z.object({
  key: z.string().min(1),
  label: KeenLabel,
  reason: KeenLabelReason.nullable().optional(),
});
export type PutLabelRequest = z.infer<typeof PutLabelRequest>;

export const LabelReviewFilter = z.enum(['all', 'unsure', 'disagree']);
export type LabelReviewFilter = z.infer<typeof LabelReviewFilter>;

export const LabelReviewRow = z.object({
  key: z.string(),
  source: LabelSource,
  projectName: z.string(),
  context: z.array(LabelLine).min(1),
  mine: z.object({ label: KeenLabel, reason: KeenLabelReason.nullable() }).nullable(),
  others: z.array(z.object({ name: z.string(), label: KeenLabel })),
  /** Whether the stored probability met the decision threshold, not proof of delivered speech. */
  deciderSpoke: z.boolean().nullable(),
  /** Recorded decision lifecycle, not proof of completed playback. Absent on older servers. */
  deciderOutcome: KeenOutcome.nullable().optional(),
});
export type LabelReviewRow = z.infer<typeof LabelReviewRow>;

export const LabelReviewResponse = z.object({ rows: z.array(LabelReviewRow) });
export type LabelReviewResponse = z.infer<typeof LabelReviewResponse>;

/** Training export (EXPORT_TOKEN): every labelled moment with the exact decider input. */
export const KeenLabelExportItem = z.object({
  key: z.string(),
  source: LabelSource,
  projectId: z.uuid(),
  /** What the decider is sent for this moment. */
  transcript: z.string().min(1),
  /** The live decider's P(yes), when stored. */
  deciderP: z.number().min(0).max(1).nullable(),
  /** What became of the live decision, when recorded: a timeout here is a moment the decider never judged. */
  deciderOutcome: KeenOutcome.nullable().optional(),
  labels: z.array(
    z.object({ userId: z.string(), label: KeenLabel, reason: KeenLabelReason.nullable(), updatedAt: z.string() }),
  ),
});
export type KeenLabelExportItem = z.infer<typeof KeenLabelExportItem>;

export const KeenLabelExportResponse = z.object({
  items: z.array(KeenLabelExportItem),
  nextOffset: z.number().int().nonnegative().nullable(),
});
export type KeenLabelExportResponse = z.infer<typeof KeenLabelExportResponse>;
